import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import medicinesData from "@/data/supplement-check/medicines.json";
import { ServingStepper } from "@/components/supplement/ServingStepper";
import { SupplementListControl } from "@/components/supplement/SupplementListControl";
import { QuestionProgress } from "@/components/supplement/QuestionProgress";

const medicineOptions = [
  ...medicinesData.medicine_classes.map((item) => ({ id: item.id, label: item.label })),
  { id: "none", label: "None" },
];

const isComplete = (question, value) => {
  if (question.type === "single") return typeof value === "string" && value.length > 0;
  if (question.type === "grid") return value && question.rows.every((row) => typeof value[row.id] === "number");
  if (question.type === "list") return Array.isArray(value) && value.every((row) => row.ingredient && row.amount_per_day > 0 && row.unit);
  if (question.type === "multi") return Array.isArray(value) && (question.id === "q_goals" || value.length > 0);
  return false;
};

export const QuestionStep = ({ question, questionNumber, questionCount, value, onChange, onBack, onNext }) => {
  const toggleMulti = (option) => {
    const current = Array.isArray(value) ? value : [];
    if (option === "none") {
      onChange(current.includes("none") ? [] : ["none"]);
      return;
    }
    const withoutNone = current.filter((item) => item !== "none");
    if (withoutNone.includes(option)) {
      onChange(withoutNone.filter((item) => item !== option));
      return;
    }
    if (question.max_select && withoutNone.length >= question.max_select) return;
    onChange([...withoutNone, option]);
  };

  const renderControl = () => {
    if (question.type === "single") {
      return (
        <div className="supplement-options" role="radiogroup" data-testid={`supplement-options-${question.id}`}>
          {Object.entries(question.options).map(([id, label]) => (
            <button type="button" key={id} className={value === id ? "is-selected" : ""} aria-pressed={value === id} onClick={() => onChange(id)} data-testid={`supplement-${question.id}-option-${id}`}>{label}</button>
          ))}
        </div>
      );
    }
    if (question.type === "multi") {
      const options = question.id === "q_medicines" ? medicineOptions : Object.entries(question.options).map(([id, label]) => ({ id, label }));
      const selected = Array.isArray(value) ? value : [];
      return (
        <div className="supplement-multi" data-testid={`supplement-options-${question.id}`}>
          {options.map((option) => {
            const maxed = Boolean(question.max_select && selected.length >= question.max_select && !selected.includes(option.id));
            return (
              <label key={option.id} className={selected.includes(option.id) ? "is-selected" : ""}>
                <Checkbox checked={selected.includes(option.id)} disabled={maxed} onCheckedChange={() => toggleMulti(option.id)} data-testid={`supplement-${question.id}-option-${option.id}`} />
                <span>{option.label}</span>
              </label>
            );
          })}
        </div>
      );
    }
    if (question.type === "grid") {
      return (
        <div className="supplement-grid" data-testid={`supplement-grid-${question.id}`}>
          {question.rows.map((row) => (
            <div className="supplement-grid-row" key={row.id} data-testid={`supplement-grid-row-${row.id}`}>
              <span data-testid={`supplement-grid-label-${row.id}`}>{row.text}</span>
              <ServingStepper id={row.id} label={row.text} value={value?.[row.id] ?? row.default ?? 1} onChange={(next) => onChange({ ...value, [row.id]: next })} />
            </div>
          ))}
        </div>
      );
    }
    return <SupplementListControl question={question} value={value} onChange={onChange} />;
  };

  return (
    <section className="supplement-tool" data-testid={`supplement-question-${question.id}`}>
      <QuestionProgress current={questionNumber} total={questionCount} />
      <h2 data-testid="supplement-question-text">{question.text}</h2>
      {renderControl()}
      <div className="supplement-actions">
        <Button variant="ghost" onClick={onBack} data-testid="supplement-question-back-button">Back</Button>
        <Button onClick={onNext} disabled={!isComplete(question, value)} className="supplement-primary-action" data-testid="supplement-question-next-button">Next</Button>
      </div>
    </section>
  );
};